import { useState, useEffect, useRef } from 'react';
import { useInView, useReducedMotion } from 'framer-motion';
import { Play, Pause, RotateCcw, MousePointerClick } from 'lucide-react';
import {
  RemotionCompositionContext,
  RECORDLY_CHAPTERS,
  RECORDLY_ZOOM_KEYFRAMES,
  RECORDLY_CURSOR_WAYPOINTS,
  computeRecordlyCamera,
  computeRecordlyCursor,
} from './remotionRecordlyEngine';
import { RecordlyDemoScenes } from './RecordlyDemoScenes';

const TOTAL_FRAMES = 720;
const FPS = 30;

export function RecordlyVideoDemoPlayer() {
  const [frame, setFrame] = useState(65);
  const shouldReduceMotion = useReducedMotion();
  const containerRef = useRef<HTMLDivElement>(null);
  const inView = useInView(containerRef, { amount: 0.25 });
  const [isPlaying, setIsPlaying] = useState(!shouldReduceMotion);
  const [autoZoomEnabled, setAutoZoomEnabled] = useState(true);
  const [interactiveMode, setInteractiveMode] = useState(false);

  useEffect(() => {
    if (!isPlaying || interactiveMode || !inView) return undefined;
    const intervalMs = Math.max(16, Math.round(1000 / FPS));
    const timer = window.setInterval(() => {
      setFrame((prev) => (prev + 1) % TOTAL_FRAMES);
    }, intervalMs);
    return () => window.clearInterval(timer);
  }, [isPlaying, interactiveMode, inView]);

  const camera = computeRecordlyCamera(frame, FPS, RECORDLY_ZOOM_KEYFRAMES, autoZoomEnabled);
  const cursor = computeRecordlyCursor(frame, FPS, RECORDLY_CURSOR_WAYPOINTS);
  const currentChapter =
    RECORDLY_CHAPTERS.find((c) => frame >= c.startFrame && frame <= c.endFrame) ||
    RECORDLY_CHAPTERS[0];

  const currentSeconds = Math.floor(frame / FPS);
  const totalSeconds = Math.floor(TOTAL_FRAMES / FPS);
  const formatTime = (sec: number) => `0:${sec.toString().padStart(2, '0')}`;

  const handleAdvanceHotspot = () => {
    const nextWaypoint =
      RECORDLY_CURSOR_WAYPOINTS.find((w) => w.frame > frame + 15 && w.click) ||
      RECORDLY_CURSOR_WAYPOINTS[1];
    setFrame(nextWaypoint.frame + 4);
  };

  // Map cursor coordinates through the same camera transform as the viewport (scale about center, then pan).
  const cursorX = 50 + (cursor.x - 50) * camera.scale + camera.panX;
  const cursorY = 50 + (cursor.y - 50) * camera.scale + camera.panY;

  return (
    <div
      ref={containerRef}
      data-testid="recordly-video-demo-player"
      className="w-full rounded-2xl bg-white border border-gray-200 shadow-[0_16px_48px_rgba(11,17,32,0.08)] overflow-hidden"
    >
      {/* Flat Chapter Navigation Rail — 2x2 on mobile, horizontal bar on desktop, zero truncation */}
      <div
        role="tablist"
        aria-label="Product walkthrough chapters"
        className="grid grid-cols-2 sm:flex sm:items-center border-b border-gray-200 bg-[#FAFAFC] px-2 sm:px-6"
      >
        {RECORDLY_CHAPTERS.map((chap, idx) => {
          const isCurrent = currentChapter.id === chap.id;
          const zoomKf = RECORDLY_ZOOM_KEYFRAMES[idx];
          return (
            <button
              key={chap.id}
              type="button"
              role="tab"
              aria-selected={isCurrent}
              aria-label={`${chap.title} (${zoomKf?.label ?? ''})`}
              onClick={() => {
                setAutoZoomEnabled(true);
                setFrame(chap.startFrame + 60);
              }}
              className={`relative min-h-11 py-3 px-3 sm:px-4 text-xs sm:text-sm text-left sm:text-center transition-colors ${
                isCurrent
                  ? 'font-semibold text-[#0B1120]'
                  : 'font-normal text-[#676879] hover:text-[#0B1120]'
              }`}
            >
              <span>{chap.title}</span>
              {isCurrent && (
                <span className="absolute inset-x-2 sm:inset-x-0 bottom-0 h-0.5 bg-[#0B1120]" />
              )}
            </button>
          );
        })}
      </div>

      {/* Unobstructed Full-Frame Real Web-App Viewport (sidebar & header always 100% intact) */}
      <div className="relative aspect-[16/10] w-full overflow-hidden bg-[#F5F5F2]">
        <div
          data-zoom-active={Boolean(camera.activeKeyframe && autoZoomEnabled)}
          className="w-full h-full origin-center"
          style={{
            transform: `translate(${camera.panX}%, ${camera.panY}%) scale(${camera.scale})`,
          }}
        >
          <RemotionCompositionContext.Provider
            value={{
              frame,
              fps: FPS,
              durationInFrames: TOTAL_FRAMES,
              width: 1440,
              height: 900,
            }}
          >
            <RecordlyDemoScenes />
          </RemotionCompositionContext.Provider>
        </div>

        {/* Click-Through Hotspot when user switches to interactive step mode */}
        {interactiveMode && (
          <button
            type="button"
            aria-label="Click target to advance"
            onClick={handleAdvanceHotspot}
            style={{
              left: `${Math.min(76, Math.max(24, cursorX))}%`,
              top: `${Math.min(78, Math.max(22, cursorY))}%`,
            }}
            className="absolute -translate-x-1/2 -translate-y-1/2 z-30 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#0B1120] text-white text-xs font-medium shadow-lg ring-2 ring-[#FBC72D] hover:scale-105 transition-transform max-w-[88vw] truncate"
          >
            <MousePointerClick className="size-3.5 text-[#FBC72D] shrink-0" />
            <span className="truncate">{cursor.actionLabel}</span>
          </button>
        )}

        {/* Subtle Pointer Cursor */}
        <div
          aria-hidden="true"
          style={{
            left: `${cursorX}%`,
            top: `${cursorY}%`,
            transform: `translate(-2px, -2px) scale(${cursor.clickScale})`,
            transition: 'left 90ms linear, top 90ms linear, transform 90ms ease-out',
          }}
          className="pointer-events-none absolute z-20 hidden sm:block"
        >
          {cursor.isClicking && (
            <span
              style={{
                transform: `scale(${0.7 + cursor.rippleProgress * 0.9})`,
                opacity: Math.max(0, 0.7 - cursor.rippleProgress * 0.7),
              }}
              className="absolute -left-2 -top-2 size-6 rounded-full border border-[#0B1120] bg-[#FBC72D]/30"
            />
          )}

          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            className="drop-shadow-[0_2px_4px_rgba(0,0,0,0.25)]"
          >
            <path
              d="M4 3L19 10.5L11.5 13L9 20.5L4 3Z"
              fill="#0B1120"
              stroke="#FFFFFF"
              strokeWidth="2"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>

      {/* Minimal Flat Video Transport Bar */}
      <div className="px-4 sm:px-6 py-3 bg-white border-t border-gray-200 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            aria-label={isPlaying ? 'Pause demo video' : 'Play demo video'}
            onClick={() => {
              setInteractiveMode(false);
              setIsPlaying((prev) => !prev);
            }}
            className="size-11 rounded-lg bg-[#0B1120] text-white flex items-center justify-center hover:bg-[#1e293b] transition-colors"
          >
            {isPlaying && !interactiveMode ? (
              <Pause className="size-3.5" />
            ) : (
              <Play className="size-3.5 ml-0.5" />
            )}
          </button>

          <button
            type="button"
            aria-label="Restart demo video"
            onClick={() => {
              setInteractiveMode(false);
              setFrame(0);
              setIsPlaying(true);
            }}
            className="size-11 rounded-lg border border-gray-200 text-[#161616] flex items-center justify-center hover:bg-gray-50 transition-colors"
          >
            <RotateCcw className="size-3.5" />
          </button>

          <span className="text-xs font-medium text-[#676879] tabular-nums">
            {formatTime(currentSeconds)} / {formatTime(totalSeconds)}
          </span>
        </div>

        <div className="flex-1 min-w-[140px] max-w-xl mx-2">
          <input
            type="range"
            min="0"
            max={TOTAL_FRAMES - 1}
            value={frame}
            onChange={(e) => {
              setIsPlaying(false);
              setFrame(Number(e.target.value));
            }}
            aria-label="Scrub video timeline"
            className="w-full accent-[#0B1120] cursor-pointer block"
          />
        </div>

        <div className="flex items-center gap-2 text-xs">
          <button
            type="button"
            aria-label="Toggle Recordly Auto-Zoom"
            aria-pressed={autoZoomEnabled}
            onClick={() => setAutoZoomEnabled((prev) => !prev)}
            className={`min-h-11 px-2.5 py-1 rounded border text-xs font-medium transition-colors ${
              autoZoomEnabled
                ? 'border-[#0B1120] bg-[#0B1120] text-white'
                : 'border-gray-200 bg-white text-[#676879]'
            }`}
          >
            Focus Lock
          </button>

          <button
            type="button"
            onClick={() => {
              const next = !interactiveMode;
              setInteractiveMode(next);
              setIsPlaying(!next);
            }}
            className={`min-h-11 px-2.5 py-1 rounded border text-xs font-medium transition-colors ${
              interactiveMode
                ? 'border-[#0B1120] bg-[#FBC72D] text-[#161616]'
                : 'border-gray-200 bg-white text-[#161616] hover:bg-gray-50'
            }`}
          >
            Interactive Click-Through
          </button>
        </div>
      </div>
    </div>
  );
}
